import { useState, useEffect, useContext, createContext } from 'react';
import { useLocation } from 'react-router-dom';
import { Center, Spinner, Box, Text } from '@chakra-ui/react';
import { PrivateRoute } from './PrivateRoute';
import Header from './Header';
import API from '../helpers/axiosInstance';
import { getApiHeaders, hasAnyPermission, hasPermission } from '../utils/DefaultFunction';

// PERM-163: lets a page rendered under <PermissionRoute> read the SAME
// already-fetched permission list via context instead of calling
// usePagePermissions(sameGroup) again itself and duplicating the network
// call PermissionRoute already made. Pages not rendered under
// PermissionRoute (or not opted in) get `null` from the hook below and can
// safely fall back to fetching on their own.
export const PermissionRouteContext = createContext(null);

export function usePermissionRouteContext() {
    return useContext(PermissionRouteContext);
}

// Route-level permission gate for pages reachable by direct/bookmarked URL
// (detail, create and edit pages) that previously only went through
// <PrivateRoute> (login check only, no permission check). Wrap the page's
// route element in this instead of/around <PrivateRoute> — it fetches
// `group`'s permission list itself and only mounts `children` (so only then
// does the page's own data-fetching effects run) once at least one slug in
// `slug` is granted. Until then it shows a spinner, then "No Access".
//
// `group` is the permission group slug to fetch (the page's own base route,
// hardcoded — never derived from the current URL, since detail/edit pages
// carry an :id segment that would never match any real group).
// `slug` is a single slug string, or an array of slugs where any one grants
// access (e.g. a view-or-edit pair) — the page itself still has to apply its
// own finer edit/create slug check to decide what's actually editable.
//
// PERM-190: `allowIfState` is an optional extra grant for routes whose normal
// `slug` deliberately excludes a create-only slug (so create permission alone
// can't open ANY existing record by guessing/bookmarking its URL) — e.g.
// `/flow/:id` requires flow-edit/flow-list, not flow-create. A page that
// navigates here right after creating a new record (passing router state,
// e.g. `{ justCreated: true }`) can still open THAT ONE record via
// `allowIfState={{ key: 'justCreated', slug: 'flow-create' }}`: granted only
// when `location.state?.[key] === true` AND the user holds `slug`. Since
// `location.state` never survives a refresh/bookmark/shared link, reopening
// the same URL later falls back to the normal `slug` check alone.
//
// `requireAll` flips `slug` from any-of to all-of — used by create pages
// that also need the module's list slug (RP_005/RP_007), so a direct URL
// can't open a create page the list screen's own button wouldn't show.
function PermissionRoute({ group, slug, allowIfState, requireAll = false, children }) {
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const location = useLocation();

    useEffect(() => {
        let cancelled = false;
        const fetchPermissions = async () => {
            setLoading(true);
            try {
                const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${group}`;
                const response = await API.get(url, { headers: getApiHeaders() });
                if (!cancelled) setPermissions(response.data.data || []);
            } catch (error) {
                console.error('Error fetching permissions:', error);
                if (!cancelled) setPermissions([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchPermissions();
        return () => { cancelled = true; };
    }, [group]);

    const slugs = Array.isArray(slug) ? slug : [slug];
    const grantedByState = Boolean(
        allowIfState &&
        location.state?.[allowIfState.key] === true &&
        hasPermission(allowIfState.slug, permissions)
    );
    const granted = grantedByState || (requireAll
        ? slugs.every((s) => hasPermission(s, permissions))
        : hasAnyPermission(slugs, permissions));

    return (
        <PrivateRoute>
            {loading ? (
                <>
                    <Header />
                    <Center h="60vh"><Spinner size="xl" /></Center>
                </>
            ) : !granted ? (
                <>
                    <Header />
                    <Center h="400px">
                        <Box textAlign="center">
                            <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                                No Access
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                You don't have permission to view this sections.
                            </Text>
                        </Box>
                    </Center>
                </>
            ) : (
                <PermissionRouteContext.Provider value={{ group, permissions, loading }}>
                    {children}
                </PermissionRouteContext.Provider>
            )}
        </PrivateRoute>
    );
}

export default PermissionRoute;
